import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import ReactionBar from "./ReactionBar";
import { AutoLink } from "./AutoLink";
import { toast } from "sonner";
import { MessageSquare, Trash2, Loader2, EyeOff, CornerDownRight } from "lucide-react";

function CommentCard({ c, isReply, articleId, onChanged, onReply }) {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return ""; }
  };
  const remove = async () => {
    try {
      await api.delete(`/comments/${c.id}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <div className={`border border-white/10 bg-card p-5 ${isReply ? "" : ""}`} data-testid={isReply ? "comment-reply" : "comment-item"}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`font-heading font-bold ${c.anonymous ? "italic text-muted-foreground" : ""}`}>{c.name}</span>
          <span className="font-mono text-[11px] text-muted-foreground">· {fmtDate(c.created_at)}</span>
        </div>
        {c.can_delete && (
          <button onClick={remove} data-testid={`delete-comment-${c.id}`} className="text-muted-foreground hover:text-lie" title={t("comments.delete")}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      <p className="whitespace-pre-line text-foreground/90"><AutoLink text={c.text} /></p>
      <div className="mt-3 flex items-center gap-4">
        <ReactionBar targetType="comment" targetId={c.id} likes={c.likes} dislikes={c.dislikes} mine={c.my_reaction} testidPrefix={`comment-${c.id}`} />
        {!isReply && user && user !== false && (
          <button onClick={() => onReply(c.id)} data-testid={`reply-btn-${c.id}`} className="flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary">
            <CornerDownRight className="h-3.5 w-3.5" /> {t("comments.reply")}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Comments({ articleId }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const isAuthed = user && user !== false;
  const count = comments.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);

  const load = () => {
    setLoading(true);
    api.get(`/articles/${articleId}/comments`).then(({ data }) => setComments(data)).finally(() => setLoading(false));
  };

  useEffect(() => { if (articleId) load(); /* eslint-disable-next-line */ }, [articleId, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post("/comments", { article_id: articleId, text });
      setText("");
      toast.success(t("comments.posted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      await api.post("/comments", { article_id: articleId, text: replyText, parent_id: replyTo });
      setReplyText("");
      setReplyTo(null);
      toast.success(t("comments.posted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  return (
    <section className="mt-16 border-t border-white/10 pt-10" data-testid="comments-section">
      <h2 className="flex items-center gap-2 font-heading text-2xl font-black tracking-tight">
        <MessageSquare className="h-6 w-6 text-primary" /> {t("comments.title")} <span className="text-muted-foreground">({count})</span>
      </h2>

      {isAuthed ? (
        <form onSubmit={submit} className="mt-6">
          <Textarea data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={t("comments.placeholder")} className="border-white/15 bg-card" />
          <div className="mt-3 flex items-center justify-between">
            {user.anonymous ? (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><EyeOff className="h-3.5 w-3.5" /> {t("comments.anonNote")}</span>
            ) : <span />}
            <Button type="submit" disabled={posting || !text.trim()} data-testid="comment-submit" className="rounded-md bg-primary font-bold uppercase tracking-wider hover:bg-[#FF645A]">
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("comments.submit")}
            </Button>
          </div>
        </form>
      ) : (
        <p className="mt-6 text-sm text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline">{t("nav.login")}</Link> {t("comments.loginPrompt")}
        </p>
      )}

      <div className="mt-8 space-y-4">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : comments.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="comments-empty">{t("comments.empty")}</p>
        ) : (
          comments.map((c) => (
            <div key={c.id}>
              <CommentCard c={c} isReply={false} articleId={articleId} onChanged={load} onReply={(id) => { setReplyTo(id); setReplyText(""); }} />
              {replyTo === c.id && (
                <form onSubmit={submitReply} className="ml-8 mt-3">
                  <Textarea data-testid="reply-input" value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2} placeholder={t("comments.replyPlaceholder")} className="border-white/15 bg-card" />
                  <div className="mt-2 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setReplyTo(null)} className="border-white/15 bg-transparent">{t("common.cancel")}</Button>
                    <Button type="submit" disabled={posting || !replyText.trim()} data-testid="reply-submit" className="rounded-md bg-primary font-bold uppercase tracking-wider hover:bg-[#FF645A]">
                      {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("comments.reply")}
                    </Button>
                  </div>
                </form>
              )}
              {c.replies && c.replies.length > 0 && (
                <div className="ml-8 mt-3 space-y-3 border-l border-white/10 pl-4">
                  {c.replies.map((r) => (
                    <CommentCard key={r.id} c={r} isReply articleId={articleId} onChanged={load} onReply={() => {}} />
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
